// Employee-recorded significant dates — HR/Manager case view. Renders the
// role-projected `SignificantDatesForRole` the API returns: HR sees the full
// list (type/label/month-day, no year); Manager sees only a coarse signal,
// never the label or specific date. Mirrors `EmployeePreferencesPanel`'s
// server-side-gated posture — this component only ever receives what the
// role may see.

import {
  SIGNIFICANT_DATE_MONTH_LABELS,
  SIGNIFICANT_DATE_TYPE_LABELS,
  type SignificantDatesForRole,
} from '@besideu/shared/significant-dates';
import { Button } from './ui/Button.tsx';

export function SignificantDatesPanel({
  significantDates,
  onAskMaeve,
}: {
  significantDates: SignificantDatesForRole;
  /**
   * Manager-only (PO 2026-08-06). Offers the coarse signal's obvious follow-up
   * question — "so what do I do about it?" — to the case's Maeve guidance
   * thread. The caller owns the question text and the navigation, because the
   * caller owns the thread it lands in (`ManagerCaseDetail`).
   *
   * The ceiling is unchanged: Maeve is no better informed than the manager
   * here. Manager mode gets the same coarse `hasUpcomingSignificantDate`
   * boolean this panel renders and nothing else (`renderCaseBlock` in
   * `services/maeve.ts`), and is instructed not to have the manager probe for
   * what the date is. So the answer is about the situation, never the date.
   *
   * Omitted by HR, who can already see the list itself.
   */
  onAskMaeve?: () => void;
}) {
  return (
    <section className="rounded-lg border border-border p-6">
      <h2 className="text-lg font-semibold">Significant dates</h2>
      {significantDates.visible !== null ? (
        significantDates.visible.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">None recorded.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {significantDates.visible.map((d) => (
              <li key={d.id} className="text-sm">
                <span className="font-medium">{SIGNIFICANT_DATE_TYPE_LABELS[d.dateType]}</span>
                {' — '}
                {d.label} ({SIGNIFICANT_DATE_MONTH_LABELS[d.month - 1]} {d.day})
              </li>
            ))}
          </ul>
        )
      ) : significantDates.hasUpcoming ? (
        <>
          <p className="mt-1 text-sm text-muted-foreground">
            This employee has a personally significant date coming up soon — ask HR if you need more
            detail.
          </p>
          {onAskMaeve ? (
            <Button variant="secondary" className="mt-3 py-1.5 text-sm" onClick={onAskMaeve}>
              What should I do about this?
            </Button>
          ) : null}
        </>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">Nothing upcoming.</p>
      )}
    </section>
  );
}
